import {defaultHandlerCatch, defaultHandlerResolver, promisifyImage} from "./helpers";
import {createMd5Token} from "../../utils/text";
import {pwAssetUrl} from "../../utils/etc";
import axios from "axios";
import {photolabTask} from "../api";
import Processing from "../Processing";
import {ageGroups} from "../etc";
import * as imageHelper from "../../helpers/image.helper";
import analyticsHelper from "../../helpers/analytics.helper";
import {PhotolabTaskBuilder} from "@vicman-wi/photolab-api-js";

const axiosClient = axios.create();
const tasksCache = {};

export function normalizeHairColor(sourceColor) {
  sourceColor = (sourceColor || "").toLowerCase();
  sourceColor = sourceColor.split(" and ").first();
  sourceColor = sourceColor === "yellow" ? "blonde" : sourceColor;
  sourceColor = sourceColor === "white" ? "gray" : sourceColor;
  sourceColor = sourceColor === "brown" ? "brunette" : sourceColor;

  return sourceColor;
}

function normalizeHairStyle(string) {
  string = string.replace(/-|_/g, " ").toLowerCase();
  if (string === "bald") {
    return "bald head";
  } else {
    return string + " hair";
  }
}

export function normalizeAgeGroup(value) {
  const intValue = parseInt(value);
  const group = ageGroups.find((group) => {
    return intValue >= group.range[0] && intValue <= group.range[1];
  });

  if (group) {
    return group.id;
  }

  const strValue = ("" + value).toLowerCase();
  if (strValue === "old") {
    return "old";
  } else if (strValue === "middle aged") {
    return "adult";
  }

  return strValue;
}

export function mapAgeGroupToPromptString(ageGroup, gender) {
  const group = ageGroups.find((g) => g.id === ageGroup);
  return group
    ? group.value[gender]
    : group;
}

function photolabTaskHelper(creative, taskName, taskConfig, options) {
  const taskCacheKey = JSON.stringify(taskConfig);
  const _options = Object.assign({
    timeout: 1000,
    interval: 1000,
  }, options);

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: _options.timeout,
      interval: _options.interval,
    })
  }

  return tasksCache[taskCacheKey].then((taskResult) => {
    creative.setTask(taskName, taskResult);
    return taskResult;
  }).catch((err) => {
    delete tasksCache[taskCacheKey];
    throw err;
  });
}

async function promptTask(processing, creative) {
  const {answers} = processing.getExtra(Processing.EXTRA_QUESTIONS_TASK);
  const configId = creative.getExtra("config_id");
  const seed = creative.getExtra("seed");
  const selectedHairColor = processing.getExtra(Processing.EXTRA_SELECTED_HAIR_COLOR);

  const hairColor = (selectedHairColor && selectedHairColor !== "other")
    ? selectedHairColor
    : normalizeHairColor(answers[1]);

  const ageGroup = normalizeAgeGroup(answers[3]);

  if (!configId) {
    throw new Error("No config_id");
  }

  const gender = answers[0].split(" ").first();

  const params = {
    "@transformationId": "npp_base",
    "@configId": configId,
    param_sds: seed,
    gender: gender,
    "@replacements": [
      {
        parameters: ["prompt"],
        search: "{{hair_color}}",
        mode: "ig",
        replacement: hairColor.replace(/-|_/g, " "),
      },
      {
        parameters: ["prompt"],
        search: "{{skin_color}}",
        mode: "ig",
        replacement: answers[2].replace(/-|_/g, " "),
      },
      {
        parameters: ["prompt"],
        search: "{{hair_length}}",
        mode: "ig",
        replacement: normalizeHairStyle(processing.getExtra(Processing.EXTRA_SELECTED_HAIR_STYLE, "")),
      },
      {
        parameters: ["prompt"],
        search: "{{age_group}}",
        mode: "ig",
        replacement: mapAgeGroupToPromptString(ageGroup, gender),
      }
    ],
  };

  const paramsHash = createMd5Token(JSON.stringify(params));
  const cacheImageName = "d22sdxl_" + paramsHash + ".jpeg";
  const cacheImageUrl = pwAssetUrl("task_cache/" + cacheImageName);

  try {
    await axiosClient.head(cacheImageUrl);
    analyticsHelper.d22CacheHit();

    const tastResult = {resultUrl: cacheImageUrl};

    creative.setTaskConfig("prompt", tastResult);
    creative.setTaskConfig("prompt_crop", tastResult);

    return Promise.resolve(tastResult);
  } catch (err) {
    analyticsHelper.d22CacheMiss();
  }

  const promptTaskConfig = new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addCollageMethod({template_name: 8905, ...params})
    .addImage("https://assets.photo-cdn.net/templates-previews/wt_d19_barbie_female_6.jpg")
    .build();

  const promptTaskResult = await photolabTaskHelper(creative, "prompt", promptTaskConfig);

  const cropTaskConfig = new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addCollageMethod({template_name: 6637}) // v15
    .addImage(promptTaskResult.resultUrl)
    .build();

  const cropTaskResult = await photolabTaskHelper(creative, "prompt_crop", cropTaskConfig);

  imageHelper.store({
    disk: "task_cache",
    file: cropTaskResult.resultUrl,
    fileName: cacheImageName,
  }).then().catch();

  return cropTaskResult;
}

function finalTask(processing, creative, fileUrl, promptTaskResult) {
  const taskConfig = new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addCollageMethod({template_name: 8896})
    .addImage(fileUrl)
    .addImage(promptTaskResult.resultUrl)
    .build();

  return photolabTaskHelper(creative, "final", taskConfig);
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */
export default (processing, creative) => {
  return new Promise(async (resolve, reject) => {
    try {
      //const questionsTaskResult = await questionsTask(processing, creative, processing.file.url);
      const promptTaskResult = await promptTask(processing, creative);
      const finalTaskResult = await finalTask(processing, creative, processing.file.url, promptTaskResult);

      await promisifyImage(finalTaskResult.resultUrl);

      creative.setFile("raw", finalTaskResult.resultUrl);
      creative.markAsProcessed(finalTaskResult.resultUrl);

      defaultHandlerResolver(creative, resolve)();
    } catch (err) {
      defaultHandlerCatch(creative, reject)(err);
    }
  });
}